import React, { useState, useEffect } from 'react';
import toast from 'react-hot-toast';
import { confirmAction } from '../../utils/confirmToast';
import { Bell, CheckCircle, Info, AlertTriangle, Settings2, Trash2 } from 'lucide-react';
import PageHeader from '../../components/common/PageHeader/PageHeader';
import api from '../../api/axios';
import styles from './Notifications.module.css';

const Notifications = () => {
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterType, setFilterType] = useState('all');
  const [showFilter, setShowFilter] = useState(false);

  useEffect(() => {
    fetchNotifications();
    window.addEventListener('notificationUpdated', fetchNotifications);
    return () => window.removeEventListener('notificationUpdated', fetchNotifications);
  }, []);

  const fetchNotifications = async () => {
    try {
      setLoading(true);
      const { data } = await api.get('/users/notifications');
      setNotifications(data);
      setLoading(false);

      // Auto-mark as read if any are unread since user has viewed the page
      const hasUnread = data.some(n => !n.read);
      if (hasUnread) {
        await api.put('/users/notifications/read');
        window.dispatchEvent(new Event('notificationUpdated'));
      }
    } catch (error) {
      console.error('Failed to fetch notifications');
      setLoading(false);
    }
  };

  const markAllRead = async () => {
    try {
      await api.put('/users/notifications/read');
      setNotifications(notifications.map(n => ({ ...n, read: true })));
      toast.success('All notifications marked as read');
      window.dispatchEvent(new Event('notificationUpdated'));
    } catch (error) {
      toast.error('Failed to mark as read');
    }
  };

  const deleteNotification = async (id) => {
    confirmAction("Are you sure you want to delete this notification?", async () => {
      try {
        await api.delete(`/users/notifications/${id}`);
        setNotifications(notifications.filter(n => n._id !== id));
        toast.success('Notification deleted');
        window.dispatchEvent(new Event('notificationUpdated'));
      } catch (error) {
        toast.error('Failed to delete notification');
      }
    });
  };

  const getIcon = (type) => {
    switch (type) {
      case 'alert': return <AlertTriangle size={20} className={styles.iconAlert} />;
      case 'success': return <CheckCircle size={20} className={styles.iconSuccess} />;
      default: return <Info size={20} className={styles.iconInfo} />;
    }
  };

  const filteredNotifications = notifications.filter(n => filterType === 'all' || n.type === filterType);

  return (
    <div className={styles.container}>
      <PageHeader 
        title="Notifications" 
        breadcrumb={['Resources', 'Notifications']} 
        actionButton={
          <div className={styles.actions}>
            <button className={styles.secondaryBtn} onClick={markAllRead}>
              <CheckCircle size={16} /> Mark all as read
            </button>
            <div className={styles.filterContainer}>
              <button className={`${styles.iconBtn} ${showFilter ? styles.active : ''}`} onClick={() => setShowFilter(!showFilter)}>
                <Settings2 size={18} />
              </button>
              {showFilter && (
                <div className={styles.filterMenu}>
                  <button className={`${styles.filterItem} ${filterType === 'all' ? styles.active : ''}`} onClick={() => {setFilterType('all'); setShowFilter(false);}}>All Notifications</button>
                  <button className={`${styles.filterItem} ${filterType === 'alert' ? styles.active : ''}`} onClick={() => {setFilterType('alert'); setShowFilter(false);}}>Alerts</button>
                  <button className={`${styles.filterItem} ${filterType === 'success' ? styles.active : ''}`} onClick={() => {setFilterType('success'); setShowFilter(false);}}>Success</button>
                  <button className={`${styles.filterItem} ${filterType === 'info' ? styles.active : ''}`} onClick={() => {setFilterType('info'); setShowFilter(false);}}>General Info</button>
                </div>
              )}
            </div>
          </div>
        }
      />

      <div className={styles.list}>
        {loading ? <p>Loading notifications...</p> : filteredNotifications.length === 0 ? <p style={{ color: '#6b7280', fontSize: '14px', textAlign: 'center', padding: '40px 0' }}>No notifications found in this category.</p> : filteredNotifications.map(notif => (
          <div key={notif._id} className={`${styles.notifItem} ${!notif.read ? styles.unread : ''}`}>
            <div className={styles.iconWrapper}>
              {getIcon(notif.type)}
            </div>
            <div className={styles.content}>
              <h4>{notif.title}</h4>
              <p>{notif.message}</p>
              <span className={styles.time}>{new Date(notif.createdAt).toLocaleDateString('en-GB')}</span>
            </div>
            {!notif.read && <div className={styles.unreadDot}></div>}
            <button className={styles.deleteBtn} onClick={() => deleteNotification(notif._id)}>
              <Trash2 size={16} />
            </button>
          </div>
        ))}
      </div>
    </div>
  );
};

export default Notifications;
